import type { Track } from './score.js';
import { diatonic, midi, Score } from './score.js';

/**
 * The music (ADR-0056), as the owner chose it from the mockups: the menu's
 * bossa (A), and in play the Office Tower's marimba shuffle (B), with each
 * building after it speaking B's language — the marimba in front, a swing,
 * the bassoon's oom-pa and the pizzicato — in a mode and a voice of its own.
 *
 * Every track is a loop of 24 to 32 bars, 44 to 58 seconds. The trims were
 * measured offline: every track sits at the same loudness on a phone speaker,
 * which `music.ts` plays about 15 dB under the door chime.
 */

export type TrackId = 'menu' | 'office' | 'hotel' | 'transit' | 'skyline-1' | 'skyline-2';

function pick<T>(table: Readonly<Record<string, T>>, key: string): T {
  const value = table[key];
  if (value === undefined) throw new Error(`no ${key} in the table`);
  return value;
}

/** Each eighth of a bar, as `beat` and its place in the bar. */
const EIGHTHS = [0, 1, 2, 3, 4, 5, 6, 7] as const;

const BOSSA_BASS: Record<string, readonly [string, string]> = {
  C: ['C3', 'G2'],
  A: ['A2', 'E2'],
  D: ['D3', 'A2'],
  G: ['G2', 'D3'],
  E: ['E3', 'B2'],
  F: ['F2', 'C3'],
};

/** A bossa bar's bass: root and fifth, long and short; a bar of two chords takes each its half. */
function bossaBass(s: Score, b0: number, first: string, second: string): void {
  const [root1, fifth1] = pick(BOSSA_BASS, first.charAt(0));
  const [root2, fifth2] = pick(BOSSA_BASS, second.charAt(0));
  if (first !== second) {
    s.add(b0, 'bass', root1, 1.4, 1);
    s.add(b0 + 1.5, 'bass', fifth1, 0.4, 0.55);
    s.add(b0 + 2, 'bass', root2, 1.4, 0.95);
    s.add(b0 + 3.5, 'bass', fifth2, 0.4, 0.55);
    return;
  }
  s.add(b0, 'bass', root1, 1.4, 1);
  s.add(b0 + 1.5, 'bass', root1, 0.4, 0.5);
  s.add(b0 + 2, 'bass', fifth1, 1.4, 0.9);
  s.add(b0 + 3.5, 'bass', fifth1, 0.4, 0.5);
}

/** A bossa bar's guitar, cross-stick and shaker: two bars make the pattern. */
function bossaRhythm(
  s: Score,
  b0: number,
  even: boolean,
  first: readonly string[],
  second: readonly string[],
): void {
  for (const hit of even ? [0, 1.5, 3] : [1, 2.5]) {
    s.chord(b0 + hit, 'gtr', hit < 2 ? first : second, 0.55, hit === 0 ? 0.95 : 0.75, 0.011);
  }
  for (const hit of even ? [0.75, 2.5] : [0, 1.5, 3]) s.add(b0 + hit, 'rim', null, 0.1, 0.8);
  for (const e of EIGHTHS) s.add(b0 + e * 0.5, 'shaker', null, 0.1, e % 2 ? 0.9 : 0.5);
}

/** A: the menu. Bossa nova, the lift-music joke, in C major. */
function menu(): Track {
  const s = new Score(4);
  const prog = [
    'Cmaj7',
    'Am7',
    'Dm7',
    'G7',
    'Cmaj7',
    'Am7',
    'Dm7',
    'G7sus|G7',
    'Cmaj7',
    'Am7',
    'Dm7',
    'G7',
    'Cmaj7',
    'Am7',
    'Dm7|G7',
    'Cmaj7',
    'Fmaj7',
    'Em7',
    'Dm7',
    'Cmaj7',
    'Fmaj7',
    'Em7',
    'Dm7',
    'G7sus|G7',
  ];
  const voicing: Record<string, readonly string[]> = {
    Cmaj7: ['E3', 'G3', 'B3', 'D4'],
    Am7: ['G3', 'C4', 'E4'],
    Dm7: ['F3', 'A3', 'C4', 'E4'],
    G7: ['F3', 'A3', 'B3', 'E4'],
    G7sus: ['F3', 'A3', 'C4', 'D4'],
    Em7: ['G3', 'B3', 'D4'],
    Fmaj7: ['E3', 'G3', 'A3', 'C4'],
  };
  prog.forEach((symbol, bar) => {
    const b0 = bar * 4;
    const [first = '', second = first] = symbol.split('|');
    bossaBass(s, b0, first, second);
    bossaRhythm(s, b0, bar % 2 === 0, pick(voicing, first), pick(voicing, second));
  });
  s.line(
    'vibes',
    0,
    `
    r/1 G4/1 E4/1 G4/1 A4/2 G4/2 | E4/3 D4/1 C4/2 r/2 | r/1 A4/1 F4/1 A4/1 C5/2 B4/1 A4/1 | G4/4 r/2 F4/1 G4/1 |
    r/1 G4/1 E4/1 G4/1 B4/2 A4/2 | G4/2 E4/2 C5/3 B4/1 | A4/2 F4/1 E4/1 D4/2 F4/2 | E4/4 D4/2 r/2 |
    r/1 G4/1 E4/1 G4/1 A4/2 C5/2 | B4/3 A4/1 G4/2 E4/2 | r/1 A4/1 F4/1 A4/1 D5/2 C5/1 A4/1 | B4/4 r/2 G4/1 A4/1 |
    r/1 G4/1 E4/1 G4/1 B4/2 D5/2 | C5/3 B4/1 A4/2 G4/2 | F4/2 A4/2 G4/2 F4/1 D4/1 | C4/4 r/4 |
    A4/3 G4/1 E4/4 | G4/3 E4/1 D4/4 | F4/3 E4/1 C4/2 D4/2 | E4/6 r/2 |
    r/1 C5/1 A4/1 G4/1 A4/4 | r/1 B4/1 G4/1 E4/1 G4/4 | r/1 A4/1 F4/1 D4/1 F4/2 E4/2 | D4/4 r/4 |`,
    0.5,
    0.95,
  );
  return s.done('menu', 126, 24, 0.5, 0.127);
}

/** B: the Office Tower. A marimba shuffle in A minor, the oom-pa under it. */
function office(): Track {
  const s = new Score(4);
  const a = ['Am', 'Am', 'Dm', 'Am', 'F', 'G', 'Em', 'Am'];
  const b = ['F', 'G', 'Em', 'Am', 'Dm', 'Em', 'F', 'G'];
  const stab: Record<string, readonly string[]> = {
    Am: ['A3', 'C4', 'E4'],
    Dm: ['A3', 'D4', 'F4'],
    F: ['A3', 'C4', 'F4'],
    G: ['G3', 'B3', 'D4'],
    Em: ['G3', 'B3', 'E4'],
  };
  const low: Record<string, readonly [string, string]> = {
    Am: ['A2', 'E3'],
    Dm: ['D3', 'A2'],
    F: ['F2', 'C3'],
    G: ['G2', 'D3'],
    Em: ['E2', 'B2'],
  };
  [...a, ...b, ...a].forEach((chord, bar) => {
    const b0 = bar * 4;
    const [root, fifth] = pick(low, chord);
    s.add(b0, 'bassoon', root, 0.45, 1);
    s.add(b0 + 2, 'bassoon', fifth, 0.45, 0.85);
    s.chord(b0 + 1, 'pizz', pick(stab, chord), 0.3, 0.55);
    s.chord(b0 + 3, 'pizz', pick(stab, chord), 0.3, 0.5);
    for (const e of EIGHTHS) s.add(b0 + e * 0.5, 'shaker', null, 0.1, e % 2 ? 0.6 : 0.35);
    if (bar % 2 === 1) s.add(b0 + 3.5, 'wood', 'C5', 0.1, 0.6);
    if (bar % 4 === 3) s.add(b0 + 1.5, 'wood', 'G4', 0.1, 0.45);
  });
  const tune = `
    A4/1 r/1 C5/1 E5/1 D5/1 C5/1 A4/2 | r/1 E4/1 A4/1 B4/1 C5/2 B4/1 A4/1 | D5/1 r/1 F4/1 A4/1 D5/1 C5/1 A4/2 | C5/1 B4/1 A4/1 E4/1 A4/2 r/2 |
    A4/1 r/1 C5/1 A4/1 F4/1 A4/1 C5/2 | B4/1 r/1 D5/1 B4/1 G4/1 B4/1 D5/2 | E5/1 D5/1 B4/1 G4/1 E4/2 B4/1 C5/1 | A4/2 r/2 E4/1 A4/1 r/2 |`;
  const bridge = `
    C5/2 A4/1 F4/1 r/1 A4/1 C5/1 F5/1 | E5/2 D5/1 B4/1 r/1 G4/1 B4/1 D5/1 | E5/1 r/1 B4/1 r/1 G4/1 r/1 E4/1 r/1 | A4/1 B4/1 C5/1 D5/1 E5/2 r/2 |
    F5/1 E5/1 D5/1 A4/1 F4/2 A4/2 | G4/1 B4/1 E5/1 B4/1 G4/2 E4/2 | A4/1 C5/1 F5/1 C5/1 A4/1 F4/1 A4/1 C5/1 | D5/2 B4/1 G4/1 D5/2 r/2 |`;
  s.line('marimba', 0, tune, 0.5, 1, 1);
  s.line('marimba', 32, bridge, 0.5, 1, 1);
  s.line('marimba', 64, tune, 0.5, 1, 1);
  // The last pass, doubled an octave down on pizzicato.
  const lower = tune.replace(
    /([A-G])(\d)/g,
    (_, step: string, octave: string) => `${step}${Number(octave) - 1}`,
  );
  s.line('pizz', 64, lower, 0.5, 0.7, 1);
  return s.done('office', 104, 24, 0.62, 0.209);
}

/** H5: the Grand Hotel. B's shuffle in C major, a harpsichord breaking the chords. */
function hotel(): Track {
  const s = new Score(4);
  const a = ['C', 'Am', 'Dm', 'G', 'C', 'Am', 'F', 'G'];
  const b = ['F', 'G', 'Em', 'Am', 'Dm', 'G', 'C', 'G'];
  const broken: Record<string, readonly string[]> = {
    C: ['C4', 'G4', 'E4'],
    Am: ['A3', 'E4', 'C4'],
    Dm: ['D4', 'A4', 'F4'],
    G: ['B3', 'G4', 'D4'],
    F: ['A3', 'F4', 'C4'],
    Em: ['G3', 'E4', 'B3'],
  };
  const low: Record<string, readonly [string, string]> = {
    C: ['C3', 'G2'],
    Am: ['A2', 'E3'],
    Dm: ['D3', 'A2'],
    G: ['G2', 'D3'],
    F: ['F2', 'C3'],
    Em: ['E2', 'B2'],
  };
  const stab: Record<string, readonly string[]> = {
    C: ['G3', 'C4', 'E4'],
    Am: ['A3', 'C4', 'E4'],
    Dm: ['A3', 'D4', 'F4'],
    G: ['G3', 'B3', 'D4'],
    F: ['A3', 'C4', 'F4'],
    Em: ['G3', 'B3', 'E4'],
  };
  const order = [0, 1, 2, 1, 0, 1, 2, 1] as const;
  [...a, ...b, ...a].forEach((chord, bar) => {
    const b0 = bar * 4;
    const notes = pick(broken, chord);
    order.forEach((k, e) => {
      s.add(b0 + e * 0.5, 'harpsi', notes[k] ?? null, 0.3, e === 0 ? 0.65 : 0.5);
    });
    const [root, fifth] = pick(low, chord);
    s.add(b0, 'bassoon', root, 0.45, 1);
    s.add(b0 + 2, 'bassoon', fifth, 0.45, 0.85);
    if (bar >= 8 && bar < 16) {
      s.chord(b0 + 1, 'pizz', pick(stab, chord), 0.3, 0.5);
      s.chord(b0 + 3, 'pizz', pick(stab, chord), 0.3, 0.45);
    }
    for (const e of EIGHTHS) s.add(b0 + e * 0.5, 'shaker', null, 0.1, e % 2 ? 0.55 : 0.3);
    if (bar % 2 === 1) s.add(b0 + 3.5, 'wood', 'C5', 0.1, 0.55);
  });
  const tune = `
    G4/1 r/1 C5/1 D5/1 E5/2 C5/2 | A4/1 r/1 C5/1 D5/1 E5/1 D5/1 C5/2 | D5/1 r/1 F5/1 E5/1 D5/1 C5/1 A4/2 | B4/2 G4/2 D5/2 r/2 |
    G4/1 r/1 C5/1 D5/1 E5/2 G5/2 | E5/1 D5/1 C5/1 B4/1 A4/2 C5/2 | F5/2 E5/1 D5/1 C5/1 A4/1 F4/2 | G4/1 B4/1 D5/1 F5/1 E5/2 D5/2 |`;
  const bridge = `
    A4/2 C5/2 F5/4 | D5/2 B4/2 G4/4 | G4/2 B4/2 E5/4 | C5/2 E5/2 A4/4 |
    F4/1 A4/1 D5/1 F5/1 E5/2 D5/2 | B4/1 D5/1 G5/1 F5/1 D5/2 B4/2 | C5/2 G4/2 E5/2 C5/2 | D5/4 r/2 G4/1 B4/1 |`;
  s.line('marimba', 0, tune, 0.5, 1, 1);
  s.line('marimba', 32, bridge, 0.5, 1, 1);
  s.line('marimba', 64, tune, 0.5, 1, 1);
  return s.done('hotel', 108, 24, 0.6, 0.214);
}

/** T2: the Transit Centre. The last metro, in E Phrygian: a running pizzicato and a low clarinet. */
function transit(): Track {
  const s = new Score(4);
  const a = ['Em', 'F', 'Em', 'Dm', 'Am', 'G', 'F', 'Em'];
  const b = ['Am', 'Am', 'F', 'G', 'Am', 'Dm', 'F', 'Em'];
  const run: Record<string, readonly string[]> = {
    Em: ['E3', 'B3', 'G3'],
    F: ['F3', 'C4', 'A3'],
    Dm: ['D3', 'A3', 'F3'],
    Am: ['A3', 'E4', 'C4'],
    G: ['G3', 'D4', 'B3'],
  };
  const low: Record<string, string> = { Em: 'E2', F: 'F2', Dm: 'D3', Am: 'A2', G: 'G2' };
  const order = [0, 1, 2, 1, 0, 1, 2, 1] as const;
  [...a, ...b, ...a].forEach((chord, bar) => {
    const b0 = bar * 4;
    const notes = pick(run, chord);
    order.forEach((k, e) => {
      s.add(b0 + e * 0.5, 'pizz', notes[k] ?? null, 0.25, e === 0 ? 0.55 : 0.4);
    });
    s.add(b0, 'bassoon', pick(low, chord), 0.4, 1);
    s.add(b0 + 2.5, 'bassoon', pick(low, chord), 0.35, 0.8);
    for (const e of EIGHTHS) s.add(b0 + e * 0.5, 'shaker', null, 0.1, e % 2 ? 0.45 : 0.25);
    if (bar % 2 === 1) s.add(b0 + 3.5, 'wood', 'G4', 0.1, 0.5);
  });
  const tune = `
    E4/1 r/1 G4/1 r/1 B4/1 r/1 E5/1 r/1 | F5/2 E5/1 r/1 C5/2 A4/2 | B4/1 r/1 G4/1 B4/1 E5/2 D5/2 | F5/1 E5/1 D5/1 A4/1 F4/2 r/2 |
    A4/1 r/1 C5/1 r/1 E5/1 D5/1 C5/2 | B4/1 r/1 D5/1 B4/1 G4/2 r/2 | A4/1 C5/1 F5/1 E5/1 D5/1 C5/1 A4/1 F4/1 | E4/2 r/1 F4/1 E4/4 |`;
  const echo = `
    r/5 E5/1 C5/1 r/1 | r/5 B4/1 A4/1 r/1 | r/5 C5/1 A4/1 r/1 | r/5 D5/1 B4/1 r/1 |
    r/5 E5/1 C5/1 r/1 | r/5 F5/1 D5/1 r/1 | r/5 C5/1 A4/1 r/1 | r/5 B4/1 G4/1 r/1 |`;
  s.line('marimba', 0, tune, 0.5, 1, 1);
  s.line(
    'clarinet',
    32,
    'E4/4 C4/4 | B3/4 C4/4 | A3/4 C4/4 | B3/4 D4/4 | E4/4 C4/4 | F4/4 D4/4 | C4/4 A3/4 | B3/4 E4/4 |',
    0.5,
    1.1,
    0.95,
  );
  s.line('marimba', 32, echo, 0.5, 0.7, 1);
  s.line('marimba', 64, tune, 0.5, 1, 1);
  return s.done('transit', 124, 24, 0.57, 0.222);
}

const SKY_STAB: Record<string, readonly string[]> = {
  Fmaj7: ['A3', 'C4', 'E4'],
  'G/F': ['G3', 'B3', 'D4'],
  Em7: ['G3', 'B3', 'D4'],
  Am7: ['G3', 'C4', 'E4'],
  Dm7: ['F3', 'A3', 'C4'],
  G7sus: ['F3', 'C4', 'D4'],
  Cmaj7: ['G3', 'B3', 'E4'],
  'G/B': ['G3', 'B3', 'D4'],
};

/** S1: the Skyline Tower, odd shifts. Above the clouds in F Lydian: a flute, a pad, the wind. */
function skylineCloud(): Track {
  const s = new Score(4);
  const a = ['Fmaj7', 'G/F', 'Fmaj7', 'G/F', 'Em7', 'Am7', 'Dm7', 'G7sus'];
  const b = ['Cmaj7', 'G/B', 'Am7', 'Fmaj7', 'Dm7', 'Em7', 'Fmaj7', 'G/F'];
  const low: Record<string, readonly [string, string]> = {
    Fmaj7: ['F2', 'C3'],
    'G/F': ['F2', 'C3'],
    Em7: ['E2', 'B2'],
    Am7: ['A2', 'E3'],
    Dm7: ['D3', 'A2'],
    G7sus: ['G2', 'D3'],
    Cmaj7: ['C3', 'G2'],
    'G/B': ['B2', 'D3'],
  };
  [...a, ...b, ...a].forEach((chord, bar) => {
    const b0 = bar * 4;
    const [root, fifth] = pick(low, chord);
    s.add(b0, 'bassoon', root, 0.45, 0.8);
    s.add(b0 + 2, 'bassoon', fifth, 0.45, 0.65);
    s.chord(b0 + 1, 'pizz', pick(SKY_STAB, chord), 0.3, 0.45);
    s.chord(b0 + 3, 'pizz', pick(SKY_STAB, chord), 0.3, 0.4);
    s.chord(b0, 'pad', pick(SKY_STAB, chord), 3.9, 0.7);
    for (const e of EIGHTHS) s.add(b0 + e * 0.5, 'shaker', null, 0.1, e % 2 ? 0.45 : 0.25);
    if (bar % 8 === 4) s.add(b0, 'wind', null, 6, 1);
  });
  const tune = `
    C5/1 r/1 F5/1 r/1 E5/1 C5/1 A4/2 | B4/1 r/1 D5/1 B4/1 G4/2 B4/2 | A4/1 C5/1 E5/1 F5/1 E5/2 C5/2 | D5/3 C5/1 B4/2 G4/2 |
    G4/1 B4/1 E5/1 G5/1 E5/2 B4/2 | C5/1 E5/1 A4/1 C5/1 E5/2 r/2 | F4/1 A4/1 C5/1 F5/1 E5/1 D5/1 C5/1 A4/1 | G4/4 r/4 |`;
  s.line('marimba', 0, tune, 0.5, 1, 1);
  s.line(
    'flute',
    32,
    'E5/4 D5/2 B4/2 | D5/6 r/2 | C5/2 E5/2 A4/4 | A4/2 C5/2 E5/2 F5/2 | F5/4 E5/2 D5/2 | B4/4 G4/4 | A4/2 B4/2 C5/2 E5/2 | D5/8 |',
    0.5,
    0.63,
    0.95,
  );
  // Under the flute, the marimba breaks each chord in eighths, an octave up.
  b.forEach((chord, k) => {
    const notes = pick(SKY_STAB, chord).map((n) => midi(n) + 12);
    [0, 1, 2, 1, 0, 1, 2, 1].forEach((j, e) => {
      s.addNote((8 + k) * 4 + e * 0.5, 'marimba', notes[j] ?? 60, 0.4, 0.4);
    });
  });
  s.line('marimba', 64, tune, 0.5, 1, 1);
  return s.done('skyline-1', 100, 24, 0.58, 0.207);
}

/** S2: the Skyline Tower, even shifts and the finale. The summit in C: scales that climb, a timpani. */
function skylineSummit(): Track {
  const s = new Score(4);
  const a = ['C', 'Am', 'F', 'G', 'C', 'Em', 'F|G', 'C'];
  const b = ['Am', 'F', 'C', 'G', 'Am', 'F', 'Dm', 'G'];
  const stab: Record<string, readonly string[]> = {
    C: ['G3', 'C4', 'E4'],
    Am: ['A3', 'C4', 'E4'],
    F: ['A3', 'C4', 'F4'],
    G: ['G3', 'B3', 'D4'],
    Em: ['G3', 'B3', 'E4'],
    Dm: ['A3', 'D4', 'F4'],
  };
  const low: Record<string, readonly [string, string]> = {
    C: ['C3', 'G2'],
    Am: ['A2', 'E3'],
    F: ['F2', 'C3'],
    G: ['G2', 'D3'],
    Em: ['E2', 'B2'],
    Dm: ['D3', 'A2'],
  };
  const climb: Record<string, string> = { Am: 'A3', F: 'F3', C: 'C4', G: 'G3', Dm: 'D4' };
  [...a, ...b, ...a].forEach((symbol, bar) => {
    const b0 = bar * 4;
    const [first = '', second = first] = symbol.split('|');
    s.add(b0, 'bassoon', pick(low, first)[0], 0.45, 1);
    s.add(
      b0 + 2,
      'bassoon',
      first === second ? pick(low, first)[1] : pick(low, second)[0],
      0.45,
      0.85,
    );
    for (const e of EIGHTHS) s.add(b0 + e * 0.5, 'shaker', null, 0.1, e % 2 ? 0.5 : 0.3);
    if (bar % 4 === 0) s.add(b0, 'tom', null, 0.5, 1);
    if (bar % 4 === 3) {
      s.add(b0 + 3, 'tom', null, 0.5, 0.6);
      s.add(b0 + 3.5, 'tom', null, 0.5, 0.8);
    }
    if (bar >= 8 && bar < 16) {
      // The middle: the pizzicato climbs a scale every bar, a floor at a time.
      const start = midi(pick(climb, first));
      for (const e of EIGHTHS) s.addNote(b0 + e * 0.5, 'pizz', diatonic(start, e), 0.3, 0.5);
    } else {
      s.chord(b0 + 1, 'pizz', pick(stab, first), 0.3, 0.5);
      s.chord(b0 + 3, 'pizz', pick(stab, second), 0.3, 0.45);
    }
  });
  const tune = `
    G4/1 C5/1 E5/1 G5/1 E5/2 C5/2 | E5/2 D5/1 C5/1 A4/2 C5/2 | F4/1 A4/1 C5/1 F5/1 E5/1 D5/1 C5/2 | B4/1 r/1 D5/1 r/1 G5/2 r/2 |
    G4/1 C5/1 E5/1 G5/1 F5/1 E5/1 D5/1 C5/1 | B4/2 E5/2 G5/2 E5/2 | F5/1 E5/1 D5/1 C5/1 B4/1 C5/1 D5/2 | C5/2 r/1 G4/0.5 G4/0.5 C5/2 r/2 |`;
  const bridge = `
    E5/2 C5/2 A4/4 | F5/2 C5/2 A4/4 | G5/2 E5/2 C5/4 | D5/2 B4/2 G4/4 |
    A4/2 C5/2 E5/4 | A4/2 C5/2 F5/4 | F5/2 E5/2 D5/2 A4/2 | B4/2 D5/2 G5/2 r/2 |`;
  s.line('marimba', 0, tune, 0.5, 1, 1);
  s.line('marimba', 32, bridge, 0.5, 1, 1);
  s.line('marimba', 64, tune, 0.5, 1, 1);
  // The last pass: the clarinet a third below the marimba.
  for (const e of s.events.filter((x) => x.instrument === 'marimba' && x.beat >= 64)) {
    if (e.midi !== null)
      s.addNote(e.beat, 'clarinet', diatonic(e.midi, -2), Math.max(e.beats, 0.4), 0.55);
  }
  return s.done('skyline-2', 110, 24, 0.6, 0.219);
}

const WRITERS: Readonly<Record<TrackId, () => Track>> = {
  menu,
  office,
  hotel,
  transit,
  'skyline-1': skylineCloud,
  'skyline-2': skylineSummit,
};

export const TRACK_IDS = Object.keys(WRITERS) as TrackId[];

const written = new Map<TrackId, Track>();

/** A track, written out once on first use. */
export function track(id: TrackId): Track {
  const known = written.get(id);
  if (known) return known;
  const made = WRITERS[id]();
  written.set(id, made);
  return made;
}

/**
 * What plays where: the menu's track off shift, and in a shift its building's.
 * The Skyline Tower has two, its odd shifts on one and its even on the other,
 * so its last shift, the finale, plays the summit.
 */
export function trackFor(buildingId: string | undefined, shift: number): TrackId {
  switch (buildingId ?? 'office-tower') {
    case 'grand-hotel':
      return 'hotel';
    case 'transit-centre':
      return 'transit';
    case 'skyline-tower':
      return shift % 2 === 1 ? 'skyline-1' : 'skyline-2';
    default:
      return 'office';
  }
}
